'use client'

import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { OrgSchemaType } from '@/types/portal-api.generated'
import type { Member } from '@stytch/nextjs/b2b'
import { useStytchMember } from '@stytch/nextjs/b2b'
import { identifyUser } from '@/utilities/analytics/userAnalytics'

interface UserContextType {
  user: Member | null
  organization: OrgSchemaType | null
  setUser: (user: Member | null) => void
  setOrganization: (organization: OrgSchemaType | null) => void
  clearUserContext: () => void
}

const UserContext = createContext<UserContextType | undefined>(undefined)

export function UserProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<Member | null>(null)
  const [organization, setOrganization] = useState<OrgSchemaType | null>(null)
  const lastIdentifiedUser = useRef<string | 'anonymous' | null>(null)

  // Auto-detect existing Stytch sessions
  const { member, isInitialized } = useStytchMember()

  useEffect(() => {
    // when users are not logged in yet, identify user as anonymous

    if (!isInitialized) return
    if (member) {
      if (lastIdentifiedUser.current !== member.member_id) {
        identifyUser(member)
        lastIdentifiedUser.current = member.member_id
      }
      return
    }

    if (lastIdentifiedUser.current !== 'anonymous') {
      identifyUser(undefined)
      lastIdentifiedUser.current = 'anonymous'
    }
  }, [isInitialized, member])

  // Derive the effective user from the explicitly-set user state (e.g. from login) falling back to
  // the Stytch member (e.g. when the tab is reopened and a session is already present). This
  // replaces the guard effect that called setUser(member) inside useEffect, removing a derived
  // state effect and the react-hooks/set-state-in-effect suppression it required.
  const effectiveUser = useMemo(
    () => user ?? (isInitialized && member ? member : null),
    [user, isInitialized, member],
  )

  // Derive the effective organization the same way: prefer any explicitly-set organization (from
  // an API response) and fall back to the stub built from the Stytch member's organization_id.
  const effectiveOrganization = useMemo<OrgSchemaType | null>(() => {
    if (organization) return organization
    if (!isInitialized || !member?.organization_id) return null
    return {
      id: member.organization_id,
      name: '',
      slug: '',
      organizationId: member.organization_id,
      primaryProgramId: '',
      roles: [],
      scopes: {},
    } as OrgSchemaType
  }, [organization, isInitialized, member])

  const clearUserContext = () => {
    setUser(null)
    setOrganization(null)
  }

  const value: UserContextType = {
    user: effectiveUser,
    setUser,
    organization: effectiveOrganization,
    setOrganization,
    clearUserContext,
  }

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>
}

export function useUser() {
  const context = useContext(UserContext)
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider')
  }
  return context
}
